import {
  Range,
  Editor,
  createEditor,
  Transforms,
  Text,
  Node,
  ReactEditor,
  useSlateStatic,
  PointRef,
} from 'domains/reporter/RichTextEditor/core';
import type { ParagraphPluginElement } from '../../paragraph/types';
import {
  ParsedDictationChunk,
  ParsedDictationChunkExternalSubstitution,
  ParsedDictationChunkText,
  VoiceCommandTypeExternal,
  VoiceCommandsEditor,
  VOICE_COMMAND_TARGET_EDITOR,
  type VoiceCommandTarget,
} from './createNvoqWebsocketFactory';
import { isMacro } from '../../macroPlaceholder';
import { GET_MACRO } from 'modules/Apollo/queries';
import { client } from 'modules/Apollo/client';
import type { SlateContent, Fragment } from '../../../types';
import { createParagraph } from '../../paragraph';
import { unreachableCaseLog } from 'types';
import { usePlugins } from 'domains/reporter/RichTextEditor/hooks';
import clone from 'ramda/es/clone';
import compose from 'ramda/es/compose';
import cond from 'ramda/es/cond';
import {
  getEditorSelectionSafe,
  createEditorError,
  createEditorWarning,
  slateContentToString,
  partialEditor,
} from '../../../utils';
import { stitchNodesIntoEditor, isEmptyFragment } from '../../../stitching';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import { useInsertMacro } from '../../../../Reporter/hooks/useInsertMacro';
import { useReporterDictationStepToast } from 'hooks/useReporterDictationStepToast';
import type { Macro } from 'generated/graphql';
import { logger } from 'modules/logger';
import { capitalizeEntireFirstWord } from '../../heading/utils/normalization';
import {
  getAdvisorCommandStableTextPrefixMatch,
  usePicklistDictationSelection,
  parsePotentialPicklistCommand,
  PicklistSelectionSource,
} from '../usePicklistDictationSelection';
import type {
  GetPicklistOptionSelectionMatch,
  GetPicklistOptionSelectionFuzzyMatch,
} from '../usePicklistDictationSelection';
import {
  buildPicklistSelectionResolutionTelemetry,
  clearPicklistResolutionEmission,
  resolvePicklistSelectionCommandMode,
  resolveAcceptedPicklistSelection,
  resolvePicklistSelectionDecision,
  shouldEmitPicklistResolution,
} from '../picklistSelection/picklistSelectionDecision';
import { useRequiredFieldIndicator } from 'hooks/useRequiredFieldIndicator';
import { getEmptyTouchedRequiredFields } from '../../../utils/requiredFields';
import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import { useHeadingKeywords } from 'hooks/useHeadingKeywords';
import { annotateTextWithSource } from '../../textSourceStyling/utils';
import { DICTATION_INDEX_TEXT_MARK, DICTATION_ID_TEXT_MARK } from '../..';
import {
  buildPicklistResolutionEmissionKey,
  extractMatchedTextFromPrePost,
  normalizeTextToWords,
} from '../../picklist/utils';

import {
  isText,
  isTextAndEndsInNextNumberKeyword,
  isTextAndEndsInDigit,
  NUMBERED_LIST_REGEX,
  GO_TO_FIELD_TRIGGER_WORD,
  isTextAndContainsFieldKeyword,
  isTextAndEndsInFieldKeyword,
  isTextAndContainsPicklistKeyword,
  isTextAndEndsInPicklistKeyword,
  isMacroVoiceCommand,
  isVoiceCommandEditor,
  isExternalSubstitution,
  processTextForSimilarPhrasesToNumberedList,
  isTextAndContainsDynamicNumberedListCommand,
  extractTextFromItem,
} from './utils';
import { isTextAndEndsInDigitButNotPotentialPicklistCommand } from './utils/commandUtils';
import { StableTextResponse } from '../ASRPlex/ASRPlexProtocol';
import {
  NUMBER_KEYWORD_REGEX,
  DIGIT_REGEX,
  normalizeDecimalSpacing,
  isTextAndMaybeDecimalPattern,
  wouldFormDecimal,
  combineIntoDecimal,
  isSubstitutionStartNumberingWithDecimal,
  containsNumberDigitListPattern,
} from './utils/digitAndDecimalUtils';
import { SlateSource, slateSource } from 'domains/reporter/RichTextEditor/core/slateSource';
import { WithApolloContext } from 'modules/Apollo/types';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { FocusModeMacroResolutionStatus } from 'domains/reporter/RichTextEditor/plugins/dictation/focusMode';
import { getVoiceCommandTargetFromAction } from './utils/getVoiceCommandTargetFromAction';

export type InsertTextStep = Readonly<{
  type: 'INSERT_TEXT';
  payload: SlateContent;
}>;
export type InsertMacroStep = Readonly<{
  type: 'INSERT_MACRO';
  payload: Macro;
}>;
export type InsertVoiceCommandStep = Readonly<{
  type: 'VOICE_COMMAND';
  action: VoiceCommandsEditor;
}>;
export type InsertExternalVoiceCommandStep = Readonly<{
  type: 'EXTERNAL_VOICE_COMMAND';
  action: VoiceCommandTypeExternal;
}>;
export type Step =
  | InsertTextStep
  | InsertMacroStep
  | InsertVoiceCommandStep
  | InsertExternalVoiceCommandStep;

export type StepEntry = [Step, ParsedDictationChunk];
export type StepsQueueItem = StepEntry | null | Promise<StepEntry | null>;
export type StepsQueueItems = Array<StepsQueueItem>;

export enum MacroVoiceCommandOutcome {
  Accepted = 'accepted',
  Rejected = 'rejected',
  Noop = 'noop',
}

export enum MacroVoiceCommandSource {
  Substitution = 'substitution',
  Picklist = 'picklist',
}

type CreateStepsOptions = {
  fieldNames: Array<string>;
  enablePicklistDictation: boolean;
  getPicklistOptionSelectionMatch: GetPicklistOptionSelectionMatch;
  getPicklistOptionSelectionFuzzyMatch?: GetPicklistOptionSelectionFuzzyMatch;
  /** optional picklist id from dictation registration, otherwise uses current active picklist */
  picklistId?: string | null;
  /** allow triggerless picklist-option-by-name matching/deferral for the current picklist entry */
  allowTriggerlessOptionNameMatch?: boolean;
  /** optional fallback transcript from parallel advisor (e.g. Web Speech API) */
  picklistAdvisorText?: string | null;
  source?: SlateSource;
};

/**
 * Checks if a step entry contains a 'GoToField' command that potentially matches
 * or partially matches any of the provided field names.
 *
 * @param {StepEntry} stepEntry - The step entry to check.
 * @param {Array<string>} fieldNames - A list of field names to match against.
 * @returns {boolean} True if there's a potential match; false otherwise.
 */
export const containsPotentiallyIncompleteFieldCommand = (
  item: ParsedDictationChunk,
  fieldNames: Array<string>
): boolean => {
  if (fieldNames == null) {
    return false;
  }

  if (isTextAndContainsFieldKeyword(item) === false) {
    return false;
  }

  const textAfterKeyword = (item as ParsedDictationChunkText).payload.data?.text
    .slice(
      (item as ParsedDictationChunkText).payload.data?.text.indexOf(GO_TO_FIELD_TRIGGER_WORD) +
        GO_TO_FIELD_TRIGGER_WORD.length
    )
    .trim()
    .toLowerCase();

  if (textAfterKeyword == null) {
    return false;
  }

  const fieldNamesLowerCase = fieldNames.map((fieldName) => fieldName.toLowerCase().trim());

  return fieldNamesLowerCase.some((fieldName) => {
    if (textAfterKeyword === fieldName) {
      return false;
    } else if (fieldName.startsWith(textAfterKeyword)) {
      return true;
    } else if (textAfterKeyword.startsWith(fieldName)) {
      // If the text starts with a complete field name and has additional content,
      const remainingText = textAfterKeyword.slice(fieldName.length).trim();
      return remainingText.length === 0;
    }
    return false;
  });
};

export const containsPotentiallyIncompletePicklistCommand = (
  item: ParsedDictationChunk,
  picklistOptionNames: Array<string>,
  options?: {
    allowOptionNameOnly?: boolean;
  }
): boolean => {
  if (picklistOptionNames.length === 0) {
    return false;
  }

  const text = (item as ParsedDictationChunkText).payload.data?.text?.toLowerCase().trim();
  if (text == null || text.length === 0) {
    return false;
  }

  const words = text.split(/\s+/);
  const picklistCommand = parsePotentialPicklistCommand(words);

  const wordsAfterCommand =
    picklistCommand != null
      ? normalizeTextToWords(words.slice(picklistCommand.wordIndex + 1).join(' '))
      : normalizeTextToWords(text);

  // No trigger-word command and no in-picklist context: no trigger-less deferral.
  if (picklistCommand == null && !options?.allowOptionNameOnly) {
    return false;
  }

  // Existing completed numeric commands should not be deferred.
  if (picklistCommand != null) {
    const pickIndexFromNextWord = parseInt(words[picklistCommand.wordIndex + 1] ?? '', 10) - 1;
    if (pickIndexFromNextWord >= 0) {
      return false;
    }
  }

  // "pick" by itself is already covered by ends-in-keyword deferral.
  if (wordsAfterCommand.length === 0) {
    return false;
  }

  const normalizedOptionWordLists = picklistOptionNames
    .map((name) => normalizeTextToWords(name))
    .filter((optionWords) => optionWords.length > 0);

  // Defer only when dictated words are a strict prefix of a known option name.
  return normalizedOptionWordLists.some(
    (optionWords) =>
      optionWords.length > wordsAfterCommand.length &&
      wordsAfterCommand.every((word, index) => optionWords[index] === word)
  );
};

export const handlePicklistOptionSelectionVoiceCommand = (
  item: ParsedDictationChunkText,
  {
    enablePicklistDictation,
    fieldNames,
    getPicklistOptionSelectionMatch,
    getPicklistOptionSelectionFuzzyMatch,
    picklistId,
    allowTriggerlessOptionNameMatch,
    picklistAdvisorText,
  }: CreateStepsOptions
): StepsQueueItems => {
  // Keep fuzzy matcher optional for direct/unit call sites that only stub exact matching.
  // If omitted, fuzzy contributes no candidate instead of forcing every caller to pass a noop.
  const getFuzzyMatch =
    getPicklistOptionSelectionFuzzyMatch ??
    (() => null as ReturnType<GetPicklistOptionSelectionFuzzyMatch>);
  if (enablePicklistDictation === false) {
    return [handleStableText(item)];
  }

  const dictationId = item.payload.id;
  const text = item.payload.data?.text ?? '';
  const normalizedAdvisorText = picklistAdvisorText?.trim() ?? '';
  const {
    stableSelectionMatch,
    advisorSelectionMatch,
    stableMatchType,
    advisorMatchType,
    stablePickIndex,
    advisorPickIndex,
    decisionRule,
  } = resolvePicklistSelectionDecision({
    text,
    picklistId,
    allowTriggerlessOptionNameMatch,
    normalizedAdvisorText,
    getPicklistOptionSelectionMatch,
    getFuzzyMatch,
  });

  const { selectionSource, selectionMatch } = resolveAcceptedPicklistSelection({
    stableSelectionMatch,
    advisorSelectionMatch,
    stableMatchType,
    decisionRule,
    stableSource: PicklistSelectionSource.StableText,
    advisorSource: PicklistSelectionSource.WebSpeechAdvisor,
  });

  // If no selection is accepted after decision + resolution strategy, handle stable text as normal.
  if (selectionMatch == null || selectionSource == null) {
    if (item.payload.data?.textDone) {
      clearPicklistResolutionEmission(dictationId);
    }
    return [handleStableText(item)];
  }

  const selectedInputText =
    selectionSource === PicklistSelectionSource.StableText ? text : normalizedAdvisorText;
  const matchedSelectionText = extractMatchedTextFromPrePost({
    text: selectedInputText,
    preMatchText: selectionMatch.preMatchText,
    postMatchText: selectionMatch.postMatchText,
  });
  // Guard against malformed/degenerate matches that consume no text.
  // If we recurse on unchanged text, createSteps can loop until stack overflow.
  if (matchedSelectionText === '') {
    if (item.payload.data?.textDone) {
      clearPicklistResolutionEmission(dictationId);
    }
    return [handleStableText(item)];
  }
  const matchedAdvisorText =
    advisorSelectionMatch != null
      ? extractMatchedTextFromPrePost({
          text: normalizedAdvisorText,
          preMatchText: advisorSelectionMatch.preMatchText,
          postMatchText: advisorSelectionMatch.postMatchText,
        })
      : '';
  const matchedStableText =
    stableSelectionMatch != null
      ? extractMatchedTextFromPrePost({
          text,
          preMatchText: stableSelectionMatch.preMatchText,
          postMatchText: stableSelectionMatch.postMatchText,
        })
      : '';
  const stableCommandPrefixMatchForAdvisor =
    selectionSource === PicklistSelectionSource.WebSpeechAdvisor && matchedAdvisorText !== ''
      ? getAdvisorCommandStableTextPrefixMatch({
          stableText: text,
          advisorCommandText: matchedAdvisorText,
        })
      : null;
  const telemetry = buildPicklistSelectionResolutionTelemetry({
    source: selectionSource,
    decisionRule,
    stablePickIndex,
    advisorPickIndex,
    stableMatchType,
    advisorMatchType,
    selectedInputText,
    stableText: text,
    stablePickText: matchedStableText === '' ? null : matchedStableText,
    advisorText: normalizedAdvisorText === '' ? null : normalizedAdvisorText,
    advisorPickText: matchedAdvisorText === '' ? null : matchedAdvisorText,
    commandMode: resolvePicklistSelectionCommandMode(matchedSelectionText),
  });
  const emissionKey = buildPicklistResolutionEmissionKey({
    picklistId,
    source: telemetry.source,
    pickIndex: selectionMatch.pickIndex,
    decisionRule: telemetry.decisionRule,
    commandMode: telemetry.commandMode,
    stablePickText: telemetry.stablePickText,
    advisorPickText: telemetry.advisorPickText,
  });
  const shouldEmitResolution = shouldEmitPicklistResolution({ dictationId, emissionKey });

  if (shouldEmitResolution) {
    logger.info('[handlePicklistOptionSelectionVoiceCommand] picklist option selection resolved', {
      source: telemetry.source,
      picklistId,
      pickIndex: selectionMatch.pickIndex,
      decisionRule: telemetry.decisionRule,
      hasConsensus: telemetry.hasConsensus,
      stableMatchType: telemetry.stableMatchType,
      stablePickIndex: telemetry.stablePickIndex,
      stablePickText: telemetry.stablePickText,
      advisorMatchType: telemetry.advisorMatchType,
      advisorPickIndex: telemetry.advisorPickIndex,
      stableText: telemetry.stableText,
      advisorText: telemetry.advisorText,
      advisorPickText: telemetry.advisorPickText,
      selectedInputText: telemetry.selectedInputText,
      commandMode: telemetry.commandMode,
      fuzzyUsed: telemetry.fuzzyUsed,
      advisorUsed: telemetry.advisorUsed,
      selectedPreMatchText: selectionMatch.preMatchText,
      selectedPostMatchText: selectionMatch.postMatchText,
    });

    analytics.track(reporter.sys.picklistOptionSelectionResolved, {
      source: telemetry.source,
      decisionRule: telemetry.decisionRule,
      hasConsensus: telemetry.hasConsensus,
      stableMatchType: telemetry.stableMatchType,
      advisorMatchType: telemetry.advisorMatchType,
      commandMode: telemetry.commandMode,
      fuzzyUsed: telemetry.fuzzyUsed,
      advisorUsed: telemetry.advisorUsed,
    });
  }

  if (
    shouldEmitResolution &&
    selectionSource === PicklistSelectionSource.WebSpeechAdvisor &&
    advisorSelectionMatch != null &&
    text !== ''
  ) {
    logger.info(
      '[handlePicklistOptionSelectionVoiceCommand] advisor transcript matched picklist selection',
      {
        nvoqText: text,
        advisorText: matchedAdvisorText,
        picklistId,
        pickIndex: advisorSelectionMatch.pickIndex,
        advisorStableConsumedPrefixText:
          stableCommandPrefixMatchForAdvisor?.consumedPrefixText ?? null,
        advisorStableCommandPrefixConfidence:
          stableCommandPrefixMatchForAdvisor?.confidence ?? null,
      }
    );
  }

  // delete entry when we know its resolved for sure
  // this is to prevent the map from growing indefinitely with different dictation ids
  if (item.payload.data?.textDone) {
    clearPicklistResolutionEmission(dictationId);
  }

  const steps: Array<StepsQueueItem> = [];
  const { pickIndex, preMatchText, postMatchText } = selectionMatch;
  const stablePostMatchTextForAdvisor = stableCommandPrefixMatchForAdvisor?.postMatchText ?? '';
  const shouldInsertPostMatchText =
    (selectionSource === PicklistSelectionSource.StableText && postMatchText.length > 0) ||
    stablePostMatchTextForAdvisor.length > 0;

  const textAfterSelection =
    selectionSource === PicklistSelectionSource.WebSpeechAdvisor
      ? stablePostMatchTextForAdvisor
      : postMatchText;

  if (selectionSource === PicklistSelectionSource.StableText && preMatchText.length > 0) {
    const preMatchItem = createParsedDictationChunkText(item, preMatchText);
    const preMatchSteps = createSteps(preMatchItem, {
      fieldNames,
      enablePicklistDictation,
      getPicklistOptionSelectionMatch,
      getPicklistOptionSelectionFuzzyMatch,
      picklistId,
      allowTriggerlessOptionNameMatch,
    });
    steps.push(...preMatchSteps);
  }

  steps.push([
    { type: 'VOICE_COMMAND', action: 'PicklistOptionSelection' },
    {
      type: 'SUBSTITUTION',
      payload: {
        target: 'EDITOR',
        action: 'PicklistOptionSelection',
        pickIndex,
        ...(picklistId != null && { picklistId }),
        originalTextStepEntry: handleStableText(
          createParsedDictationChunkText(item, matchedSelectionText)
        )!,
      },
    },
  ]);

  if (shouldInsertPostMatchText) {
    const postMatchItem = createParsedDictationChunkText(item, textAfterSelection);
    const postMatchSteps = createSteps(postMatchItem, {
      fieldNames,
      enablePicklistDictation,
      getPicklistOptionSelectionMatch,
      getPicklistOptionSelectionFuzzyMatch,
      picklistId,
      allowTriggerlessOptionNameMatch,
    });
    steps.push(...postMatchSteps);
  }

  return steps;
};

type FieldNameMatch = {
  originalName: string;
  preMatchText: string;
  postMatchText: string;
};

export const getFieldNameMatch = (text: string, fieldNames: string[]): FieldNameMatch | null => {
  const generateFieldNameVariants = (fieldName: string) => {
    // Tracks unique altered names to prevent duplicates
    const uniqueAlteredNames = new Set();

    // If a new variant is added, please make sure to add it to the 'getFieldNameMatch' tests
    const variants = [
      fieldName.toLowerCase().trim(),
      fieldName.replace(/\s+/g, '').toLowerCase(),
      fieldName.replace(/2/gi, ' to').toLowerCase().trim(),
      fieldName.replace(/2/gi, ' too').toLowerCase().trim(),
      fieldName.replace(/3/gi, ' free').toLowerCase().trim(),
      fieldName.replace(/4/gi, ' for').toLowerCase().trim(),
      fieldName.replace(/(\d+)/g, ' $1').replace(/\s+/g, ' ').toLowerCase().trim(),
    ];

    return variants.reduce<Array<any>>((acc, alteredName) => {
      if (!uniqueAlteredNames.has(alteredName)) {
        uniqueAlteredNames.add(alteredName);
        acc.push({ originalName: fieldName, alteredName });
      }
      return acc;
    }, []);
  };

  // Sort by length of altered name so that we match the longest name first
  const fieldNameMappings = fieldNames
    .flatMap(generateFieldNameVariants)
    .sort((a, b) => b.alteredName.length - a.alteredName.length);

  const normalizedText = text.toLowerCase();

  for (const { originalName, alteredName } of fieldNameMappings) {
    const matchIndex = normalizedText.indexOf(alteredName);
    // If the altered name is found in the text, then we have a match. Return the contextual info needed to create the steps
    if (matchIndex !== -1) {
      const fieldIndex = text.toLowerCase().indexOf(GO_TO_FIELD_TRIGGER_WORD);
      const fieldToEndText = text.slice(fieldIndex);

      const preMatchText = text.slice(0, fieldIndex).trim();
      const postMatchText = fieldToEndText
        .slice(`${GO_TO_FIELD_TRIGGER_WORD} `.length + alteredName.length)
        .trim();

      return { preMatchText, originalName, postMatchText };
    }
  }

  return null;
};

export const stepEntriesTextToString = (stepEntries: StepEntry[]): string => {
  return stepEntries
    .flatMap((stepEntry) => {
      const [step] = stepEntry;

      if (step.type === 'INSERT_TEXT') {
        return step.payload.map((fragment: Node) => {
          return Node.string(fragment);
        });
      }

      return null;
    })
    .filter(Boolean)
    .join(' ');
};

const createParsedDictationChunkText = (
  item: ParsedDictationChunkText,
  newText: string
): ParsedDictationChunkText => ({
  ...item,
  payload: {
    ...item.payload,
    data: {
      ...item.payload.data,
      text: newText,
      substitutedText: newText,
    },
  },
});

export const handleNextNumberDynamicVoiceCommand = (
  item: ParsedDictationChunkText,
  options: CreateStepsOptions
): StepsQueueItems => {
  const text = processTextForSimilarPhrasesToNumberedList(item.payload.data?.text ?? '');

  const steps: Array<StepsQueueItem> = [];
  const match = NUMBERED_LIST_REGEX.exec(text);

  if (match != null) {
    const numberedListText = match[0];
    const preMatchIndex = match.index;
    const preMatchText = text.slice(0, preMatchIndex).trim();

    // #3 -> 2 or 3. -> 2 and convert from string to number
    const numberedListIndex = parseInt(numberedListText.replace(/[^\d]/g, ''), 10) - 1;

    if (preMatchText.length > 0) {
      const preMatchItem = createParsedDictationChunkText(item, preMatchText);

      const preMatchSteps = createSteps(preMatchItem, options);
      steps.push(...preMatchSteps);
    }

    // If the index is 0, then we are creating a new numbered list
    if (numberedListIndex === 0) {
      steps.push([
        { type: 'VOICE_COMMAND', action: 'StartNumbering' },
        {
          type: 'SUBSTITUTION',
          payload: {
            target: 'EDITOR',
            action: 'StartNumbering',
            originalTextStepEntry: handleStableText(
              createParsedDictationChunkText(item, numberedListText)
            ),
          },
        },
      ]);
    } else {
      steps.push([
        { type: 'VOICE_COMMAND', action: 'NextNumberDynamic' },
        {
          type: 'SUBSTITUTION',
          payload: {
            target: 'EDITOR',
            action: 'NextNumberDynamic',
            listItemIndex: numberedListIndex,
            originalTextStepEntry: handleStableText(
              createParsedDictationChunkText(item, numberedListText)
            ),
          },
        },
      ]);
    }

    const postMatchText = text.slice(preMatchIndex + numberedListText.length).trim();
    if (postMatchText.length > 0) {
      const postMatchItem = createParsedDictationChunkText(item, postMatchText);

      // Check if postMatch contains another numbered list pattern
      // If so, recursively process it to find all patterns in this chunk
      const processedPostMatchText = processTextForSimilarPhrasesToNumberedList(postMatchText);
      if (NUMBERED_LIST_REGEX.test(processedPostMatchText)) {
        // Recursively process to find all numbered list patterns
        const postMatchSteps = handleNextNumberDynamicVoiceCommand(postMatchItem, options);
        steps.push(...postMatchSteps);
      } else {
        // No more patterns, handle as regular text
        const postMatchSteps = createSteps(postMatchItem, options);
        steps.push(...postMatchSteps);
      }
    }

    return steps;
  }

  // if there is no match, then handle stable text as normal
  return [handleStableText(item)];
};

export const handleGoToFieldVoiceCommand = (
  item: ParsedDictationChunkText,
  {
    fieldNames,
    enablePicklistDictation,
    getPicklistOptionSelectionMatch,
    getPicklistOptionSelectionFuzzyMatch,
    picklistId,
    allowTriggerlessOptionNameMatch,
  }: CreateStepsOptions
): StepsQueueItems => {
  const text = item.payload.data?.text ?? '';

  const fieldNameMatch = getFieldNameMatch(text, fieldNames);

  if (fieldNameMatch != null) {
    const steps: Array<StepsQueueItem> = [];
    const { originalName, preMatchText, postMatchText } = fieldNameMatch;

    if (preMatchText.length > 0) {
      const preMatchItem = createParsedDictationChunkText(item, preMatchText);
      const preMatchSteps = createSteps(preMatchItem, {
        fieldNames,
        enablePicklistDictation,
        getPicklistOptionSelectionMatch,
        getPicklistOptionSelectionFuzzyMatch,
        picklistId,
        allowTriggerlessOptionNameMatch,
      });
      steps.push(...preMatchSteps);
    }

    steps.push([
      { type: 'VOICE_COMMAND', action: 'GoToField' },
      {
        type: 'SUBSTITUTION',
        payload: {
          target: 'EDITOR',
          action: 'GoToField',
          fieldName: originalName,
        },
      },
    ]);

    if (postMatchText.length > 0) {
      const postMatchItem = createParsedDictationChunkText(item, postMatchText);
      const postMatchSteps = createSteps(postMatchItem, {
        fieldNames,
        enablePicklistDictation,
        getPicklistOptionSelectionMatch,
        getPicklistOptionSelectionFuzzyMatch,
        picklistId,
        allowTriggerlessOptionNameMatch,
      });
      steps.push(...postMatchSteps);
    }

    return steps;
  }

  // if there is no match, then handle stable text as normal
  return [handleStableText(item)];
};

const handleMacroVoiceCommand = async (
  item: ParsedDictationChunk,
  source?: SlateSource
): Promise<StepEntry | null> => {
  if (
    item.type !== 'SUBSTITUTION' ||
    (item as ParsedDictationChunkExternalSubstitution).payload.smid == null
  )
    return null;

  const macroSmid = (item as ParsedDictationChunkExternalSubstitution).payload.smid;
  const isFocusMode = source === slateSource.ai.focusMode;
  const queryStartTime = performance.now();
  logger.info('[useSteps] Resolving macro voice command', {
    macroSmid,
    action: (item as ParsedDictationChunkExternalSubstitution).payload.action,
  });

  // PERF: Send off query soon as we know we have a substitution so that it
  // hopefully resolves before the user finishes recording at a given selection
  return client
    .query({
      query: GET_MACRO,
      variables: { id: macroSmid },
      fetchPolicy: 'network-only',
      context: {
        telemetry: { fetchPolicy: 'network-only' },
      },
    })
    .then((response) => {
      const durationMs = performance.now() - queryStartTime;
      const maybeMacro = response?.data?.macro;
      if (maybeMacro == null) {
        logger.warn('[useSteps] Macro voice command returned no macro', {
          macroSmid,
          durationMs,
        });
        analytics.track(reporter.usr.voiceCommand, {
          commandType: 'InsertMacro',
          is_focus_mode: isFocusMode,
          command_target: VOICE_COMMAND_TARGET_EDITOR,
          command_outcome: MacroVoiceCommandOutcome.Rejected,
          macro_smid: macroSmid,
          macro_source: MacroVoiceCommandSource.Substitution,
        });
        if (isFocusMode) {
          analytics.track(reporter.sys.focusModeMacroResolution, {
            macro_smid: macroSmid,
            status: FocusModeMacroResolutionStatus.NotFound,
            duration_ms: durationMs,
          });
        }
        return null;
      }
      if (isMacro(maybeMacro.text)) {
        logger.info('[useSteps] Macro voice command resolved', {
          macroSmid: maybeMacro.smid,
          macroName: maybeMacro.name,
          macroTriggerPhrase: maybeMacro.triggerPhrase,
          durationMs,
        });
        if (isFocusMode) {
          analytics.track(reporter.sys.focusModeMacroResolution, {
            macro_smid: macroSmid,
            status: FocusModeMacroResolutionStatus.Success,
            duration_ms: durationMs,
          });
        }
        return [
          {
            type: 'INSERT_MACRO',
            payload: maybeMacro,
          },
          response,
        ] as unknown as StepEntry;
      }

      logger.warn('[useSteps] Macro voice command returned non-macro content', {
        macroSmid,
        durationMs,
      });
      analytics.track(reporter.usr.voiceCommand, {
        commandType: 'InsertMacro',
        is_focus_mode: isFocusMode,
        command_target: VOICE_COMMAND_TARGET_EDITOR,
        command_outcome: MacroVoiceCommandOutcome.Noop,
        macro_smid: macroSmid,
        macro_source: MacroVoiceCommandSource.Substitution,
      });
      if (isFocusMode) {
        analytics.track(reporter.sys.focusModeMacroResolution, {
          macro_smid: macroSmid,
          status: FocusModeMacroResolutionStatus.NonMacro,
          duration_ms: durationMs,
        });
      }
      return null;
    })
    .catch((error) => {
      const durationMs = performance.now() - queryStartTime;
      logger.error('[useSteps] Failed to resolve macro voice command', {
        macroSmid,
        durationMs,
        error,
      });
      analytics.track(reporter.usr.voiceCommand, {
        commandType: 'InsertMacro',
        is_focus_mode: isFocusMode,
        command_target: VOICE_COMMAND_TARGET_EDITOR,
        command_outcome: MacroVoiceCommandOutcome.Rejected,
        macro_smid: macroSmid,
        macro_source: MacroVoiceCommandSource.Substitution,
      });
      if (isFocusMode) {
        analytics.track(reporter.sys.focusModeMacroResolution, {
          macro_smid: macroSmid,
          status: FocusModeMacroResolutionStatus.Error,
          duration_ms: durationMs,
        });
      }
      return null;
    });
};

const handleVoiceCommandEditor = (item: ParsedDictationChunk): StepEntry | null => {
  if (item.type === 'SUBSTITUTION' && item.payload.target === 'EDITOR') {
    return [{ type: 'VOICE_COMMAND', action: item.payload.action }, item];
  }
  return null;
};

const handleExternalSubstitution = (item: ParsedDictationChunk): StepEntry | null => {
  if (item.type === 'SUBSTITUTION' && item.payload.target !== 'EDITOR') {
    return [{ type: 'EXTERNAL_VOICE_COMMAND', action: item.payload.action }, item];
  }
  return null;
};

const handleStableText = (item: ParsedDictationChunk): StepEntry | null => {
  if (item.type !== 'TEXT' && item.type !== 'stable_text') return null;

  let text =
    (item as StableTextResponse).payload.text ??
    (item as ParsedDictationChunkText).payload.data?.text;

  if (text == null) {
    logger.error('[handleStableText] No text found in stable text', item);
    return null;
  }

  // Normalize decimal spacing for the text
  // 1. Remove spaces within decimals: "1 . 2" -> "1.2"
  // 2. Add spaces after decimals before units: "1.2cm" -> "1.2 cm"
  text = normalizeDecimalSpacing(text);

  const parsedStableText = text
    .split(/\n/g) // Splits on newlines
    .reduce<['payload'][]>(
      (
        contentFragment:
          | Array<ParagraphPluginElement | SlateContent>
          | Array<
              | ParagraphPluginElement
              | SlateContent
              | {
                  text: string;
                }
              | {
                  children: Array<{
                    text: string;
                  }>;
                  type: 'paragraph';
                }
            >,
        item,
        index
      ) => {
        // trimming to prevent stitching of unnecessary whitespace downstream
        const text = item.trim();
        if (index === 0) {
          return [...contentFragment, { text }];
        } else if (text === '') {
          return [...contentFragment, createParagraph()];
        }

        // Automatically create a paragraph for all non-empty elements after the first one.
        // Since we're splitting on `\n`, every non-zero indexed array entry should
        // be its own paragraph.
        contentFragment.push(createParagraph());
        const previousParagraph = contentFragment[contentFragment.length - 1];

        return [
          // We're replacing the previously empty paragraph node with content
          ...contentFragment.slice(0, contentFragment.length - 1),
          createParagraph(`${previousParagraph.children[0].text}${item}`),
        ];
      },
      []
    );

  return [{ type: 'INSERT_TEXT', payload: parsedStableText }, item];
};

export const getDeferredSteps = (
  stepEntries: StepEntry[],
  {
    fieldNames,
    editor,
    picklistOptionNames = [],
    picklistId,
    allowTriggerlessOptionNameMatch,
  }: {
    fieldNames: Array<string>;
    editor: Editor;
    picklistOptionNames?: Array<string>;
    picklistId?: string | null;
    allowTriggerlessOptionNameMatch?: boolean;
  }
): StepEntry[] => {
  const allowOptionNameOnly = allowTriggerlessOptionNameMatch ?? picklistId != null;

  for (let i = stepEntries.length - 1; i >= 0; i--) {
    const stepEntry = stepEntries[i];
    if (
      isTextAndMaybeDecimalPattern(stepEntry[1]) ||
      isSubstitutionStartNumberingWithDecimal(stepEntry[1])
    ) {
      return stepEntries.slice(i, stepEntries.length);
    }
    if (stepEntriesTextToString([stepEntry]) !== '') {
      //continuing the list so should not defer to check if it might be a decimal
      const text = extractTextFromItem(stepEntry[1]);
      const processedText = text ? processTextForSimilarPhrasesToNumberedList(text) : null;
      const hasNumberDigitPattern =
        (text && containsNumberDigitListPattern(text)) ||
        (processedText && containsNumberDigitListPattern(processedText));

      // already contains the complete pattern, don't defer
      if (isTextAndEndsInNextNumberKeyword(stepEntry[1]) && hasNumberDigitPattern) {
        return [];
      }

      if (
        isTextAndEndsInFieldKeyword(stepEntry[1]) ||
        containsPotentiallyIncompleteFieldCommand(stepEntry[1], fieldNames) ||
        isTextAndEndsInPicklistKeyword(stepEntry[1]) ||
        containsPotentiallyIncompletePicklistCommand(stepEntry[1], picklistOptionNames, {
          allowOptionNameOnly,
        }) ||
        isTextAndEndsInNextNumberKeyword(stepEntry[1]) ||
        isTextAndEndsInDigitButNotPotentialPicklistCommand(stepEntries, i, editor)
      ) {
        return stepEntries.slice(i, stepEntries.length);
      } else {
        return [];
      }
    }
  }
  return [];
};

export const maybeMergeAdjacentTextChunks = (
  parsedDictationChunks: ParsedDictationChunk[],
  fieldNames: Array<string>,
  picklistOptionNames: Array<string> = [],
  picklistId?: string | null,
  allowTriggerlessOptionNameMatch?: boolean
): ParsedDictationChunk[] => {
  const mergedSteps: Array<ParsedDictationChunk> = [];

  const allowOptionNameOnly = allowTriggerlessOptionNameMatch ?? picklistId != null;

  for (let i = 0; i < parsedDictationChunks.length; i++) {
    const current = parsedDictationChunks[i];

    if (current.type === 'TEXT') {
      const currentText = current.payload.data.text;
      const currentSubstitutedText = current.payload.data.substitutedText;

      if (i > 0) {
        const last = mergedSteps[mergedSteps.length - 1];

        // Check if last is a SUBSTITUTION with StartNumbering or NextNumberDynamic that should be converted to decimal
        // (i.e. previous step was "1." or "2." that could form a decimal)
        if (
          last &&
          last.type === 'SUBSTITUTION' &&
          last.payload.target === 'EDITOR' &&
          wouldFormDecimal(currentText)
        ) {
          const lastPayload = last.payload as any;
          if (
            (lastPayload.action === 'StartNumbering' ||
              lastPayload.action === 'NextNumberDynamic') &&
            lastPayload.originalTextStepEntry &&
            Array.isArray(lastPayload.originalTextStepEntry)
          ) {
            // Extract the original "1." or "2." from the SUBSTITUTION chunk
            const originalTextChunk = lastPayload.originalTextStepEntry[1];
            if (originalTextChunk && originalTextChunk.type === 'TEXT') {
              const originalText = originalTextChunk.payload.data.text;
              if (isTextAndMaybeDecimalPattern(originalTextChunk)) {
                // Combine "1." with "2 cm..." to form "1.2 cm..."
                const combinedText = combineIntoDecimal(originalText, currentText);
                const combinedSubstitutedText = combineIntoDecimal(
                  originalTextChunk.payload.data.substitutedText,
                  currentSubstitutedText
                );

                // Convert to TEXT chunk and update in place
                (mergedSteps[mergedSteps.length - 1] as any) = {
                  type: 'TEXT',
                  payload: originalTextChunk.payload,
                };
                const lastAsText = mergedSteps[mergedSteps.length - 1] as ParsedDictationChunkText;
                lastAsText.payload.data.text = combinedText;
                lastAsText.payload.data.substitutedText = combinedSubstitutedText;
                continue;
              }
            }
          }
        }

        // Only consider merging if current and last step are text type
        if (last && last.type === 'TEXT') {
          if (
            last.payload.data.text === '' ||
            (isTextAndEndsInFieldKeyword(last) &&
              (currentText === '' || (currentText.length > 0 && currentText[0] === ' '))) ||
            (isTextAndEndsInPicklistKeyword(last) &&
              (currentText === '' || (currentText.length > 0 && currentText[0] === ' '))) ||
            (containsPotentiallyIncompletePicklistCommand(last, picklistOptionNames, {
              allowOptionNameOnly,
            }) &&
              (currentText === '' || (currentText.length > 0 && currentText[0] === ' ')))
          ) {
            last.payload.data.text += currentText;
            last.payload.data.substitutedText += currentSubstitutedText;
            continue;
            // Merge current chunk into last if last ends in the field keyword
          } else if (
            isTextAndEndsInFieldKeyword(last) ||
            containsPotentiallyIncompleteFieldCommand(last, fieldNames) ||
            isTextAndEndsInPicklistKeyword(last) ||
            containsPotentiallyIncompletePicklistCommand(last, picklistOptionNames, {
              allowOptionNameOnly,
            })
          ) {
            last.payload.data.text += ` ${currentText}`;
            last.payload.data.substitutedText += ` ${currentSubstitutedText}`;
            continue;
          } else if (
            isTextAndEndsInDigit(last) &&
            (currentText.trim().startsWith('.') || currentText.trim() === '')
          ) {
            last.payload.data.text += currentText.trim();
            last.payload.data.substitutedText += currentSubstitutedText.trim();
            continue;
          } else if (isTextAndEndsInNextNumberKeyword(last)) {
            let trimmedText = last.payload.data.text.trim();

            if (DIGIT_REGEX.test(currentText) && NUMBER_KEYWORD_REGEX.test(trimmedText)) {
              // Replace 'number' with '#' and trim any extra whitespace
              trimmedText = trimmedText.replace(/\bnumber\b$/i, '#').trim();

              // Append the current text after the replaced text
              last.payload.data.text = `${trimmedText}${currentText.trim()}`;
              last.payload.data.substitutedText = `${trimmedText}${currentSubstitutedText.trim()}`;
            }
            continue;
          }
        }
      }
    }
    // Add the current chunk to the mergedSteps if it's not merged with the previous one
    mergedSteps.push(current);
  }

  // Post-process: Normalize decimal spacing for ALL chunks
  // This ensures consistent spacing for both single and merged chunks
  // 1. Remove spaces within decimals: "1 . 2" -> "1.2"
  // 2. Add spaces after decimals before units: "1.2cm" -> "1.2 cm"
  // List detection happens AFTER this, so "1. CT shows 2.5 cm nodule" will still match
  for (const step of mergedSteps) {
    if (step.type === 'TEXT' && step.payload.data) {
      step.payload.data.text = normalizeDecimalSpacing(step.payload.data.text);
      step.payload.data.substitutedText = normalizeDecimalSpacing(
        step.payload.data.substitutedText
      );
    }
  }

  return mergedSteps;
};

const createSteps = (
  parsedFragment: ParsedDictationChunk,
  options: CreateStepsOptions
): StepsQueueItems =>
  cond([
    [isMacroVoiceCommand, () => [handleMacroVoiceCommand(parsedFragment, options.source)]],
    [isVoiceCommandEditor, () => [handleVoiceCommandEditor(parsedFragment)]],
    [
      isTextAndContainsPicklistKeyword,
      () =>
        handlePicklistOptionSelectionVoiceCommand(
          parsedFragment as ParsedDictationChunkText,
          options
        ),
    ],
    [
      isTextAndContainsFieldKeyword,
      () => handleGoToFieldVoiceCommand(parsedFragment as ParsedDictationChunkText, options),
    ],
    [
      isTextAndContainsDynamicNumberedListCommand,
      () => {
        return handleNextNumberDynamicVoiceCommand(
          parsedFragment as ParsedDictationChunkText,
          options
        );
      },
    ],
    [isExternalSubstitution, () => [handleExternalSubstitution(parsedFragment)]],
    [
      isText,
      () => {
        if (options.enablePicklistDictation) {
          return handlePicklistOptionSelectionVoiceCommand(
            parsedFragment as ParsedDictationChunkText,
            options
          );
        }
        return [handleStableText(parsedFragment)];
      },
    ],
  ])(parsedFragment);

/**
 * TODO: This won't be needed when we field components are removed.
 * When unfurling, fragment content is wrapped in nodes to make
 * it valid against the reporter type
 * [{ type: 'field', children: [{ type: 'paragraph', children: slateContent }] }]
 *
 * This digs down to get the unfurled content to return
 */
const getFragmentContentFromTempEditor = (
  editor: Editor,
  {
    unwrapFirstChild,
  }: {
    unwrapFirstChild: boolean;
  } = { unwrapFirstChild: false }
): Fragment => {
  const editorChildren: any = editor.children;
  // If there is only one block node, return the children of the block node
  if (editor.children?.length === 1) {
    return editorChildren?.[0]?.children;
  }

  const firstBlockNode = editor.children?.[0];
  const firstBlockNodeChildren = firstBlockNode?.children;

  // Omit first node if it's a new line
  if (
    Array.isArray(firstBlockNodeChildren) &&
    firstBlockNodeChildren.length === 1 &&
    Node.isNode(firstBlockNode) &&
    Node.string(firstBlockNode) === ''
  ) {
    const [, ...otherChildren] = editor.children;
    return otherChildren;
  }

  if (unwrapFirstChild && editor.children[0].type === 'paragraph') {
    const [initialParagraph, ...otherChildren] = editor.children;
    return [
      ...(Array.isArray(initialParagraph.children) ? initialParagraph.children : []),
      ...otherChildren,
    ];
  }

  return editor.children;
};

const handleVoiceCommandStep = (editor: Editor, step: InsertVoiceCommandStep) => {};

export const useSteps = ({
  context,
}: WithApolloContext): {
  createStepsFromStableText: (
    ParsedDictationChunk: ParsedDictationChunk[],
    arg2: {
      fieldNames: string[];
      enablePicklistDictation: boolean;
      picklistOptionNames?: string[];
      picklistId?: string | null;
      allowTriggerlessOptionNameMatch?: boolean;
      picklistAdvisorText?: string | null;
      source?: SlateSource;
    }
  ) => Promise<StepEntry[]>;
  resolveSteps: (maybeSteps: StepsQueueItems) => Promise<StepEntry[]>;
  buildFragmentFromSteps: (
    steps: StepEntry[],
    source?: SlateSource,
    dictationId?: string,
    dictationIndex?: number
  ) => Fragment;
  applySteps: (arg1: {
    steps: StepEntry[];
    editor: Editor;
    at: Range;
    select?: boolean;
    source: SlateSource;
    [DICTATION_ID_TEXT_MARK]: string;
    [DICTATION_INDEX_TEXT_MARK]: number;
    autoCorrectDictionary: Record<string, string>;
  }) => Nullable<PointRef>;
} => {
  // Get the enhancers from the editor that is currently enabled to make sure steps adhere to
  // the same ruleset as the editor.
  const { getEditorStateEnhancers } = usePlugins();
  const insertMacro = useInsertMacro();
  const { enqueueDictationStepToast } = useReporterDictationStepToast();
  const editor = useSlateStatic();
  const { getPicklistOptionSelectionMatch, getPicklistOptionSelectionFuzzyMatch } =
    usePicklistDictationSelection({ editor });
  const { onInsertTextRequiredField } = useRequiredFieldIndicator();
  const { enqueueToast } = useToasterDispatch();
  const headingKeywords = useHeadingKeywords({ context });
  const [isReporterDictationMarksEnabled] = useFeatureFlagEnabled(FF.REPORTER_DICTATION_MARKS);

  const createStepsFromStableText = async (
    _parsedDictationChunks: ParsedDictationChunk[],
    {
      fieldNames,
      enablePicklistDictation,
      picklistOptionNames = [],
      picklistId,
      allowTriggerlessOptionNameMatch,
      picklistAdvisorText,
      source,
    }: {
      fieldNames: string[];
      enablePicklistDictation: boolean;
      picklistOptionNames?: string[];
      picklistId?: string | null;
      allowTriggerlessOptionNameMatch?: boolean;
      picklistAdvisorText?: string | null;
      source?: SlateSource;
    }
  ): Promise<StepEntry[]> => {
    const parsedDictationChunks = maybeMergeAdjacentTextChunks(
      _parsedDictationChunks,
      fieldNames,
      picklistOptionNames,
      picklistId,
      allowTriggerlessOptionNameMatch
    );

    // Advisor text is only a picklist-selection fallback; once we receive a <srna> substitution,
    // that fragment and all subsequent ones should not inherit stale advisor command words.
    let hasSeenSubstitution = false;
    const createdStepEntries = parsedDictationChunks.flatMap((parsedFragment) => {
      if (parsedFragment.type === 'SUBSTITUTION') {
        hasSeenSubstitution = true;
      }

      return createSteps(parsedFragment, {
        fieldNames,
        enablePicklistDictation,
        getPicklistOptionSelectionMatch,
        getPicklistOptionSelectionFuzzyMatch,
        picklistId,
        allowTriggerlessOptionNameMatch,
        picklistAdvisorText: hasSeenSubstitution ? null : picklistAdvisorText,
        source,
      });
    });

    return resolveSteps(createdStepEntries);
  };

  /**
   * Resolves all potential steps, but does not apply them to the editor or create a fragment
   * for stitching.
   */
  const resolveSteps = async (maybeSteps: StepsQueueItems): Promise<StepEntry[]> => {
    // NOTE: nVoq handles the spaces between the dictations
    try {
      const resolvedMaybeSteps = await Promise.all(maybeSteps);

      // I'm casting here because flow won't cast the array of maybe types to not maybe types based on the predicate
      const steps = resolvedMaybeSteps.filter((maybeStep) => maybeStep != null) as StepEntry[];

      return steps;
    } catch (error: any) {
      // Fail softly and don't crash the app since a user can dictate again
      createEditorError(error);
      logger.error('[useSteps] Error occurred when resolving steps', error);
      return [];
    }
  };

  const buildFragmentFromSteps = (
    steps: StepEntry[],
    source?: SlateSource,
    dictationId?: string,
    dictationIndex?: number
  ): Fragment => {
    // PERF: If there's no steps, return early
    if (steps.length === 0) return [{ text: '' }];
    // Bootstrap a temporary Slate singleton that we can apply operations to in order
    // to maintain the same ruleset that's defined by the target editor this resolved
    // content will be stitched into.
    const tempEditor = compose(...(getEditorStateEnhancers() as [any]))(createEditor());

    // TODO: This won't be needed when we field components are removed.
    // NOTE(mwood23) 5/31/21: This is temporary until we get rid of the fields component. I'm told
    // it's coming soon! Much code to delete ⛷
    (tempEditor as Editor).children = [createParagraph()];

    // Select the text node from above so we can run transforms at a given selection
    Transforms.select(tempEditor, [0, 0]);

    // For actions that apply to a node that follows the action, we need the action to flip a switch
    let capitalizeNextStep = false;

    const stepsToApply = steps.reduce<Array<any>>(
      (
        accu: Array<
          | StepEntry
          | Array<
              | ParsedDictationChunk
              | {
                  payload: Array<{
                    text: string;
                  }>;
                  type: 'INSERT_TEXT';
                }
            >
        >,
        stepEntry
      ) => {
        const [step] = stepEntry;

        if (capitalizeNextStep && step.type === 'INSERT_TEXT') {
          capitalizeNextStep = false;
          const capitalizedStep: Step = {
            ...step,
            payload: [
              {
                text: capitalizeEntireFirstWord(slateContentToString(step.payload)),
              },
            ],
          };
          accu = [...accu, [capitalizedStep, stepEntry[1] as ParsedDictationChunk]];
          return accu;
        }

        if (step.type !== 'VOICE_COMMAND') {
          return [...accu, [step as Step, stepEntry[1] as ParsedDictationChunk]];
        }

        const { action } = step;
        const newAccu = [...accu];

        // if the action cannot work in 'maybeDoVoiceCommand()', put it here:
        switch (action) {
          // TODO 13th Sep 2023 - nVoq formatting commands are currently overriding this.
          case 'AllCaps':
            capitalizeNextStep = true;
            return newAccu;
          default:
            unreachableCaseLog(action);
            return newAccu;
        }
      },
      []
    );

    stepsToApply.forEach(([step]: [any]) => {
      switch (step.type) {
        case 'VOICE_COMMAND':
          enqueueDictationStepToast(
            `[useSteps] - Voice command triggered: '${step.action ?? 'n/a'}'`
          );
          analytics.track(reporter.usr.voiceCommand, { commandType: step.action });
          return handleVoiceCommandStep(tempEditor, step);
        case 'EXTERNAL_VOICE_COMMAND':
          enqueueDictationStepToast(
            `[useSteps] - External Voice command triggered: '${step.action ?? 'n/a'}'`
          );
          analytics.track(reporter.usr.voiceCommand, { commandType: step.action });
          return;
        case 'INSERT_MACRO':
          const isFocusMode = source === slateSource.ai.focusMode;
          const commandTarget: VoiceCommandTarget = getVoiceCommandTargetFromAction('InsertMacro');
          analytics.track(reporter.usr.voiceCommand, {
            commandType: 'InsertMacro',
            is_focus_mode: isFocusMode,
            command_target: commandTarget,
            command_outcome: MacroVoiceCommandOutcome.Accepted,
            macro_smid: step.payload.smid,
            macro_source: MacroVoiceCommandSource.Substitution,
          });
          if (isFocusMode) {
            analytics.track(reporter.usr.focusModeMacroInserted, {
              macro_smid: step.payload.smid,
            });
          }
          enqueueDictationStepToast(
            `[useSteps] - Macro insertion voice command triggered: '${step.payload.name}'`
          );
          logger.info('[useSteps] Inserting macro step into temporary editor', {
            source,
            dictationId,
            dictationIndex,
            macroSmid: step.payload.smid,
            macroName: step.payload.name,
            macroTriggerPhrase: step.payload.triggerPhrase,
          });
          return insertMacro({
            editor: tempEditor,
            editorStateEnhancers: getEditorStateEnhancers(),
            macro: step.payload,
            at: getEditorSelectionSafe(tempEditor),
            source: source === slateSource.ai.focusMode ? source : undefined,
          });
        case 'INSERT_TEXT':
          slateContentToString(step.payload).trim().length > 0 &&
            enqueueDictationStepToast(
              `[useSteps] - Text inserted in useSteps: '${slateContentToString(step.payload)}'`
            );

          const shouldSkipDictationAnnotation =
            !isReporterDictationMarksEnabled && source === slateSource.user.dictation;

          // Apply source annotation at the step level so macro annotations aren't polluted by dictation info.
          const contentToStitch = shouldSkipDictationAnnotation
            ? step.payload
            : annotateTextWithSource(step.payload, {
                source,
                dictationId,
                dictationIndex,
              });

          return stitchNodesIntoEditor(tempEditor, contentToStitch, {
            at: getEditorSelectionSafe(tempEditor),
            preserveLeadingCase: true,
            select: true,
            shouldNameField: false, // we don't want to name the fields according to the temporary editor.
            headingKeywords,
          });
        default:
          unreachableCaseLog(step.type);
          return;
      }
    });

    const [firstStep] = steps[0];

    // We need to make sure we respect the user's intent so if a text node is the first node
    // that comes in we can't create a fragment with a wrapping paragraph node because that'll
    // cause the stitching algorithm to split nodes causing the text to go one line below their
    // desired intent.
    let unwrapFirstChild = false;
    let payloadText;

    if (firstStep.type === 'INSERT_TEXT') {
      payloadText = firstStep.payload[0];
    } else if (firstStep.type === 'INSERT_MACRO') {
      payloadText = firstStep.payload.text[0];
    }

    if (payloadText && (Text.isText(payloadText) || (tempEditor as Editor).isInline(payloadText))) {
      unwrapFirstChild = true;
    }

    const fragmentContent = getFragmentContentFromTempEditor(tempEditor, {
      unwrapFirstChild,
    });

    return clone(fragmentContent);
  };

  /**
   * Applies the first step to the editor (if necessary), create a fragment of the other steps,
   * and stitches them into the editor.
   */
  const applySteps = ({
    steps,
    editor,
    at,
    select = true,
    source,
    dictationId,
    dictationIndex,
    autoCorrectDictionary,
  }: {
    steps: StepEntry[];
    editor: Editor;
    at: Range;
    select?: boolean;
    source: SlateSource;
    dictationId: string;
    dictationIndex: number;
    autoCorrectDictionary: Record<string, string>;
  }): Nullable<PointRef> => {
    // PERF: If there's no steps, return early
    if (steps.length === 0) return;

    const fragment = buildFragmentFromSteps(steps, source, dictationId, dictationIndex);
    // If the fragment is empty, we don't want to insert anything. A common case
    // when this occurs is if a radiologist is tabbing through the report without
    // dictating, each change in selection will create an empty dictation.
    if (isEmptyFragment(fragment)) {
      if (select) {
        Transforms.select(editor, at);
      }
      return;
    }

    if (at == null) {
      const stepsErrorMessage = '[useSteps] Cannot dictate text if no location is given! Ignoring.';
      createEditorWarning(stepsErrorMessage);
      logger.error(stepsErrorMessage, {
        editor: editor != null ? partialEditor(editor) : 'null',
        steps,
      });
      return;
    }

    const emptyTouchedRequiredFields = getEmptyTouchedRequiredFields(editor, at);
    for (const [node] of emptyTouchedRequiredFields) {
      onInsertTextRequiredField(ReactEditor.findKey(editor, node));
    }

    const isInsertingMacro = steps.some(([step]) => step.type === 'INSERT_MACRO');

    const insertPointRef = stitchNodesIntoEditor(editor, fragment, {
      at,
      select,
      enableLogging: true,
      enqueueToast,
      headingKeywords,
      autoCorrectDictionary,
      isInsertingMacro,
    });
    return insertPointRef;
  };

  return { createStepsFromStableText, resolveSteps, applySteps, buildFragmentFromSteps };
};
